import {
  useCallback, useEffect, useState
} from 'react'
import {
  useAuth
} from './useAuth'
import {
  hasScopeCluster,
} from '../utils/scopeClusters'
import {
  whatsappService, slackService, smsService, imessageService, scopeService, billingService, organizationService
} from '../services/api'
import type {
  WhatsAppStatusResponse, SlackStatusResponse, SmsStatusResponse, IMessageStatusResponse, ScopeStatusResponse, SubscriptionResponse
} from '../services/api'
import type {
  MyInviteResponse
} from '../client'
import {
  FeatureFlags,
  isFeatureFlagEnabled
} from '../analytics/posthog'

export type ChannelType = 'whatsapp' | 'slack' | 'sms' | 'imessage'
export type ConnectedChannel = ChannelType | null

// Personal texting channels. Slack is the lone "work" channel and is
// deliberately excluded. Single source of truth for the personal-vs-work split
// the channel step's analytics report.
export const PERSONAL_CHANNELS: ReadonlySet<ChannelType> = new Set(['whatsapp', 'sms', 'imessage'])

export function isPersonalChannel(channel: ConnectedChannel): boolean {
  return channel != null && PERSONAL_CHANNELS.has(channel)
}

// How each channel is named to the user. Shared so the wizard's steps cannot
// drift from one another on the one word the user recognises.
export const CHANNEL_LABELS: Record<ChannelType, string> = {
  whatsapp: 'WhatsApp',
  slack: 'Slack',
  sms: 'SMS',
  imessage: 'iMessage',
}

// The onboarding billing step is gated on whether we still need to collect a
// card. Historically that was simply "no active subscription", but GET-8567
// grants business-email users a *cardless* trial at readiness, so `is_active`
// flips true before they ever enter a card — which silently skipped the billing
// step entirely (onboarding card capture fell to ~0%). We ask for a card up
// front again, but only while the deferred cardless welcome is still pending
// (~30 min from the grant, i.e. `trial_start`, mirroring
// BUSINESS_TRIAL_GRANT_DELAY_SECONDS on the backend). Once that window closes
// the gate falls away, so a user returning to finish onboarding later isn't
// trapped behind an unskippable billing step they have no card to satisfy.
export const CARDLESS_CARD_CAPTURE_WINDOW_MS = 30 * 60 * 1000

function computeNeedsBillingStep(subscription: SubscriptionResponse | null): boolean {
  // Shared team billing belongs to the admin. Never ask an organization member
  // for a personal card, regardless of preserved trial history on their
  // entitlement row.
  if (subscription?.billing_managed_by_team === true) return false
  // A card on file (a real Stripe subscription) always clears the step.
  if (subscription?.stripe_subscription_id != null) return false
  // No card on file. An active-but-cardless subscription is the auto-granted
  // cardless trial (or an admin comp): only gate it within the welcome window,
  // anchored on the grant time (== trial_start).
  if (subscription?.is_active) {
    if (subscription.trial_start == null) return false
    const trialAgeMs = Date.now() - new Date(subscription.trial_start).getTime()
    return trialAgeMs < CARDLESS_CARD_CAPTURE_WINDOW_MS
  }
  // No subscription, or an inactive/expired one → collect a card. This is also
  // the unchanged path for personal-email users, who never get a cardless trial.
  return true
}

// The mail/calendar grant is the only onboarding gate a user cannot satisfy
// from inside the wizard — it takes a Google consent round trip, and a user who
// declines on Google's screen comes back with nothing changed. The step
// derivation below used to treat that as simply "incomplete", so every mount
// re-derived the permissions step: connecting a channel bounced the user
// straight back to a question they had already answered, with no other
// affordance on screen. Remembering the decline lets the wizard move on.
//
// Local storage rather than a server field because this is wizard progress, not
// a user preference — it only has to outlive the reloads the wizard itself
// causes, and it is moot the moment the scopes are actually granted. The
// backend already assumes a scope-less user is a supported state:
// permissions_nudge_service provisions an assistant they can use without Google
// and nudges them on-channel 30 minutes later.
const PERMISSIONS_DEFERRED_KEY_PREFIX = 'catch.onboarding.permissionsDeferred'

function permissionsDeferredKey(userId: number): string {
  return `${PERMISSIONS_DEFERRED_KEY_PREFIX}:${userId}`
}

function readPermissionsDeferred(userId: number): boolean {
  try {
    return localStorage.getItem(permissionsDeferredKey(userId)) === '1'
  } catch {
    // Storage can be unavailable (Safari private mode). Reading false only
    // costs the user one more look at the step — never a trap, because the
    // in-memory flag still carries the rest of the session.
    return false
  }
}

function writePermissionsDeferred(userId: number): void {
  try {
    localStorage.setItem(permissionsDeferredKey(userId), '1')
  } catch {
    // Non-fatal, as above.
  }
}

// The deferral is scoped to a single onboarding run, so it is cleared once that
// run completes (see CompletionStep). Deactivating a user resets
// `setup_completed_at` (user_management_service), which sends a reactivated
// account back through the wizard — without this, a stale flag would make that
// second run skip the permissions step without ever asking, which is the
// silent-bypass behaviour GET-2704 was filed about. Abandoning a run mid-way
// deliberately keeps the flag: re-asking someone who already declined is the
// loop this change exists to remove.
export function clearPermissionsDeferred(userId: number): void {
  try {
    localStorage.removeItem(permissionsDeferredKey(userId))
  } catch {
    // Non-fatal, as above.
  }
}

export interface OnboardingStepInputs {
  isGoogleUser: boolean
  hasSelectedCharacter: boolean
  channelStepComplete: boolean
  permissionsStepBlocking: boolean
  needsBillingStep: boolean
  hasMailCalendarScopes: boolean
}

/**
 * Which step a wizard mount resumes on: the first one the user still owes.
 *
 * Google users have 6 steps, Microsoft users 5 (no permissions). Google order
 * is Character → Channel → Permissions (the concluded onboarding-channel-first
 * experiment, GET-8885), so the low-trust channel connect comes before the
 * mail/calendar scopes ask.
 *
 * The permissions gate uses `permissionsStepBlocking`, so a user who has
 * declined is not re-asked on every mount.
 *
 * Pure and exported so the step numbers have one definition and can be tested
 * without standing up the whole hook.
 */
export function deriveOnboardingStep({
  isGoogleUser,
  hasSelectedCharacter,
  channelStepComplete,
  permissionsStepBlocking,
  needsBillingStep,
  hasMailCalendarScopes,
}: OnboardingStepInputs): number {
  const billingStep = isGoogleUser ? 4 : 3
  const toolsStep = isGoogleUser ? 5 : 4
  const completionStep = isGoogleUser ? 6 : 5

  // What is left once every gate is behind the user.
  //
  // The tools step used to be absent here on the grounds that it is not a gate,
  // so a returning user "belongs on completion, not back in a grid they have
  // already seen". But omitting it does not defer it to the next visit, it
  // destroys it: the completion step calls completeSetup() on mount, which ends
  // onboarding for good. Until GET-11239 every user spent that one shot without
  // ever seeing the step, because Stripe's embedded checkout redirects the whole
  // tab and the rebuilt wizard resumed straight past it.
  //
  // "Already seen" needs no flag of its own: completeSetup() is the record, and
  // a user who has it never reaches this function again. Reaching it while
  // onboarding is unfinished means the step is still owed. It is skipped only
  // for a user who never granted mail/calendar — nothing was scanned for them,
  // so it would poll its budget out and then skip, ten seconds of dead wizard
  // while claiming to read who has emailed them.
  const afterGates = needsBillingStep
    ? billingStep
    : (hasMailCalendarScopes ? toolsStep : completionStep)

  if (!hasSelectedCharacter) return 1
  if (!channelStepComplete) return 2
  if (isGoogleUser && permissionsStepBlocking) return 3
  return afterGates
}

export interface OnboardingState {
  isLoading: boolean
  needsOnboarding: boolean
  hasSelectedCharacter: boolean
  // Whether the mail/calendar scopes are actually granted. This stays the
  // truth regardless of deferral — deferring moves the wizard on, it never
  // claims the grant happened.
  hasMailCalendarScopes: boolean
  // True once the user has explicitly chosen to finish setup without granting
  // mail/calendar scopes.
  permissionsDeferred: boolean
  // Whether the permissions step should still hold the wizard: scopes missing
  // AND not deferred. Consume this for routing
  // rather than negating hasMailCalendarScopes, which ignores the user's
  // answer.
  permissionsStepBlocking: boolean
  // Records the decline and unblocks the wizard. Idempotent.
  deferPermissions: () => void
  // Whether the onboarding billing (card-collection) step should still be shown.
  // See computeNeedsBillingStep — true for users without a card on file, but a
  // cardless-trial user only within the ~30-min post-grant welcome window.
  needsBillingStep: boolean
  // Oldest pending team invite addressed to this account, if any. The billing
  // step offers joining that team (billing covered by the team, no card)
  // before the individual paywall. Accepted members never get here —
  // billing_managed_by_team already clears needsBillingStep.
  teamInvite: MyInviteResponse | null
  connectedChannel: ConnectedChannel
  // Any connected channel (incl. Slack) completes the channel step.
  channelStepComplete: boolean
  currentStep: number
  whatsappStatus: WhatsAppStatusResponse | null
  slackStatus: SlackStatusResponse | null
  smsStatus: SmsStatusResponse | null
  smsFeatureEnabled: boolean
  imessageStatus: IMessageStatusResponse | null
  scopeStatus: ScopeStatusResponse | null
  isGoogleUser: boolean
  refetchChannelStatus: () => Promise<void>
}

export function useOnboardingState(): OnboardingState {
  const { user } = useAuth()
  const [isLoading, setIsLoading] = useState(true)
  const [whatsappStatus, setWhatsappStatus] = useState<WhatsAppStatusResponse | null>(null)
  const [slackStatus, setSlackStatus] = useState<SlackStatusResponse | null>(null)
  const [smsStatus, setSmsStatus] = useState<SmsStatusResponse | null>(null)
  const [smsFeatureEnabled, setSmsFeatureEnabled] = useState(false)
  const [imessageStatus, setImessageStatus] = useState<IMessageStatusResponse | null>(null)
  const [scopeStatus, setScopeStatus] = useState<ScopeStatusResponse | null>(null)
  const [subscription, setSubscription] = useState<SubscriptionResponse | null>(null)
  const [teamInvite, setTeamInvite] = useState<MyInviteResponse | null>(null)
  const [permissionsDeferred, setPermissionsDeferred] = useState(false)

  const isGoogleUser = user?.calendar_provider !== 'microsoft'
  const userId = user?.id

  const fetchStatuses = async (): Promise<void> => {
    try {
      const [waStatus, skStatus, smStatus, smsFlag, imStatus, scStatus, subStatus, myInvites] = await Promise.all([
        whatsappService.getStatus().catch(() => null),
        slackService.getStatus().catch(() => null),
        smsService.getStatus().catch(() => null),
        isFeatureFlagEnabled(FeatureFlags.SMS_CHANNEL),
        imessageService.getStatus().catch(() => null),
        isGoogleUser ? scopeService.getStatus().catch(() => null) : Promise.resolve(null),
        billingService.getSubscription().catch(() => null),
        // Pending team invites (auth-only endpoint) — feeds the billing step's
        // "join your team" offer. Errors degrade to the plain paywall.
        organizationService.getMyInvites().catch(() => []),
      ])
      setWhatsappStatus(waStatus)
      setSlackStatus(skStatus)
      setSmsStatus(smStatus)
      setSmsFeatureEnabled(smsFlag)
      setImessageStatus(imStatus)
      setScopeStatus(scStatus)
      setSubscription(subStatus)
      setTeamInvite(myInvites[0] ?? null)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (user) {
      void fetchStatuses()
    }
  }, [user])

  // Restore a previous decline. Keyed by user id so a shared browser never
  // carries one account's answer into another's wizard.
  useEffect(() => {
    if (userId == null) return
    setPermissionsDeferred(readPermissionsDeferred(userId))
  }, [userId])

  const deferPermissions = useCallback((): void => {
    if (userId != null) writePermissionsDeferred(userId)
    setPermissionsDeferred(true)
  }, [userId])

  const hasSelectedCharacter = user?.selected_character_id != null
  // Cluster-complete, not merely usable. `scopeStatus.mail_calendar` is true
  // off any Gmail plus any Calendar scope, so a partial grant read as done
  // here — which made this hook route the user PAST the permissions step, so
  // the partial-grant notice on it was unreachable for exactly the people it
  // was written for (GET-11570). A partial grant now holds the step, where the
  // notice names the missing permission and one click re-offers it.
  const hasMailCalendarScopes =
    !isGoogleUser ||
    (scopeStatus !== null && hasScopeCluster(scopeStatus, 'mail_calendar'))
  // A decline is only remembered because the permissions step comes after the
  // channel: backend permissions_nudge_service provisions a hosted mailbox for
  // a scope-less user with a channel and nudges them on it 30 minutes later,
  // so a deferrer still has a path back.
  const permissionsStepBlocking = !hasMailCalendarScopes && !permissionsDeferred
  const needsBillingStep = computeNeedsBillingStep(subscription)

  let connectedChannel: ConnectedChannel = null
  if (whatsappStatus?.connected) connectedChannel = 'whatsapp'
  else if (slackStatus?.connected) connectedChannel = 'slack'
  else if (smsStatus?.connected) connectedChannel = 'sms'
  else if (imessageStatus?.connected) connectedChannel = 'imessage'

  const channelStepComplete = connectedChannel != null

  const currentStep = deriveOnboardingStep({
    isGoogleUser,
    hasSelectedCharacter,
    channelStepComplete,
    permissionsStepBlocking,
    needsBillingStep,
    hasMailCalendarScopes,
  })

  const needsOnboarding = user?.setup_completed_at == null

  return {
    isLoading,
    needsOnboarding,
    hasSelectedCharacter,
    hasMailCalendarScopes,
    permissionsDeferred,
    permissionsStepBlocking,
    deferPermissions,
    needsBillingStep,
    teamInvite,
    connectedChannel,
    channelStepComplete,
    currentStep,
    whatsappStatus,
    slackStatus,
    smsStatus,
    smsFeatureEnabled,
    imessageStatus,
    scopeStatus,
    isGoogleUser,
    refetchChannelStatus: fetchStatuses,
  }
}
